import React from 'react';
import { Card, CardBody, CardHeader } from '@heroui/react';
import { motion } from 'framer-motion';
import { DailyActiveUsers } from '@/services/apis/analytics';
import { useTranslation } from 'react-i18next';

interface DailyActiveUsersChartProps {
  data: DailyActiveUsers[];
}

const DailyActiveUsersChart: React.FC<DailyActiveUsersChartProps> = ({ data }) => {
  const { t } = useTranslation('admin');

  const maxCount = Math.max(...data.map((d) => d.count), 1);
  const sortedData = [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
      <Card className="h-full">
        <CardHeader className="pb-2">
          <h3 className="text-lg font-semibold text-foreground">{t('dashboard.dailyActiveUsers')}</h3>
        </CardHeader>
        <CardBody className="pt-2">
          <div className="flex items-end justify-between gap-2 h-48">
            {sortedData.map((item, index) => {
              const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
              return (
                <div key={item.date} className="flex-1 flex flex-col items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{item.count}</span>
                  <motion.div
                    className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-md min-h-[4px]"
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  />
                  <span className="text-xs text-default-500 text-center whitespace-nowrap overflow-hidden text-ellipsis w-full">
                    {formatDate(item.date)}
                  </span>
                </div>
              );
            })}
          </div>
          {data.length === 0 && (
            <div className="flex items-center justify-center h-48 text-default-500">
              {t('dashboard.noActivityData')}
            </div>
          )}
        </CardBody>
      </Card>
    </motion.div>
  );
};

export default DailyActiveUsersChart;

